import {useCallback, useMemo, useState} from 'react'
import {Pressable, View} from 'react-native'
import {Image} from 'expo-image'
import  {type MatrixClient} from 'matrix-js-sdk'
import  {type ImageInfo} from 'matrix-js-sdk/lib/@types/media'

import {isWeb} from '#/platform/detection'
import {type MatrixMessageAttachment} from '#/state/matrix/hooks/useMatrixRoom'
import {mxcToHttp} from '#/state/matrix/media'
import {atoms as a, useTheme} from '#/alf'
import {Text} from '#/components/Typography'

const MAX_BUBBLE_WIDTH = isWeb ? 400 : 250
const MAX_BUBBLE_HEIGHT = 350

type Props = {
  attachment: MatrixMessageAttachment
  localUri?: string // for optimistic display before upload completes
  client: MatrixClient | null
  onPress?: () => void
}

export function SecureAttachmentBubble({
  attachment,
  localUri,
  client,
  onPress,
}: Props) {
  const t = useTheme()

  if (attachment.msgtype === 'm.image') {
    return (
      <ImageBubble
        attachment={attachment}
        localUri={localUri}
        client={client}
        onPress={onPress}
      />
    )
  }

  // Fallback for video/file/audio
  return (
    <View style={[a.p_md, a.rounded_md, t.atoms.bg_contrast_25]}>
      <Text style={[a.text_sm]}>{attachment.filename || 'Attachment'}</Text>
    </View>
  )
}

function ImageBubble({attachment, localUri, client, onPress}: Props) {
  const t = useTheme()
  const info = attachment.info as ImageInfo | undefined
  const [isLoaded, setIsLoaded] = useState(false)

  // Calculate display dimensions
  const {displayWidth, displayHeight} = useMemo(() => {
    const w = info?.w || 200
    const h = info?.h || 200
    const scale = Math.min(MAX_BUBBLE_WIDTH / w, MAX_BUBBLE_HEIGHT / h, 1)
    return {
      displayWidth: Math.round(w * scale),
      displayHeight: Math.round(h * scale),
    }
  }, [info?.w, info?.h])

  // Resolve image URLs
  const imageUri = useMemo(() => {
    // For optimistic display, use local URI
    if (localUri) return localUri
    // Otherwise resolve mxc:// to HTTP
    if (!client || !attachment.url) return null
    return mxcToHttp(
      client,
      attachment.url,
      displayWidth * 2,
      displayHeight * 2,
    )
  }, [localUri, client, attachment.url, displayWidth, displayHeight])

  const thumbnailUri = useMemo(() => {
    if (localUri) return null // Don't need thumbnail for local
    if (!client) return null
    const thumbUrl = (info as any)?.thumbnail_url
    if (!thumbUrl) return null
    return mxcToHttp(client, thumbUrl, displayWidth, displayHeight)
  }, [localUri, client, info, displayWidth, displayHeight])

  // Blurhash placeholder
  const blurhash = (info as any)?.['xyz.amorgan.blurhash'] as string | undefined

  const onLoad = useCallback(() => {
    setIsLoaded(true)
  }, [])

  return (
    <Pressable accessibilityRole="button"
      onPress={onPress}
      style={[
        a.rounded_md,
        a.overflow_hidden,
        {width: displayWidth, height: displayHeight},
      ]}>
      {/* Blurhash or thumbnail placeholder */}
      {!isLoaded && blurhash && (
        <Image
          source={{uri: `blurhash://${blurhash}`}}
          style={[
            {
              position: 'absolute',
              width: displayWidth,
              height: displayHeight,
            },
          ]}
          contentFit="cover"
          accessibilityIgnoresInvertColors
        />
      )}
      {!isLoaded && thumbnailUri && (
        <Image
          source={{uri: thumbnailUri}}
          style={[
            {
              position: 'absolute',
              width: displayWidth,
              height: displayHeight,
            },
          ]}
          contentFit="cover"
          accessibilityIgnoresInvertColors
        />
      )}

      {/* Full image */}
      {imageUri && (
        <Image
          source={{uri: imageUri}}
          style={{width: displayWidth, height: displayHeight}}
          contentFit="cover"
          onLoad={onLoad}
          transition={200}
          accessibilityIgnoresInvertColors
        />
      )}

      {/* Fallback when no image available */}
      {!imageUri && !blurhash && !thumbnailUri && (
        <View
          style={[
            a.flex_1,
            a.align_center,
            a.justify_center,
            t.atoms.bg_contrast_25,
          ]}>
          <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
            {attachment.filename || 'Image'}
          </Text>
        </View>
      )}
    </Pressable>
  )
}
